
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ReactFlow, {
  Connection,
  Node,
  XYPosition,
  Edge,
  MiniMap,
  ReactFlowInstance,
  Controls, 
  NodeProps,
  Position,
  Handle,
  useOnViewportChange,
  Viewport,
  MarkerType
} from 'reactflow';
import 'reactflow/dist/style.css';

import styled, { css } from 'styled-components';
import { BrokenWords } from "@pages/PageStructure.component";
import { MissionControlDataFlowNode, MissionControlDataFlowEdge, setViewportInfo, getViewportInfo } from '@stores/data.store';
import { autoLayout } from '@services/diagram.service';
import MissionControlConnectionLine from '../MissionControlConnectionLine';
import { MissionControlCardinalEdge, MissionControlEdgeProps } from '../edges/MissionControlEdge.component';
import { isValidConnection } from './utils';
import { Link, useNavigate } from 'react-router-dom';
import AuthService from '@services/auth/auth.service';
import { useImmer } from 'use-immer';
import { BuildException, BuildOrchestration, ModelStatus } from '@models/buildOrchestration';
import Danger from '@components/statusIndicators/Danger.component';
import { parseDateString} from '@services/time.service';
import '@szhsin/react-menu/dist/index.css';
import '@szhsin/react-menu/dist/transitions/slide.css';
import { getGroupValueForNodeType, getNodeTypeConfigFromNodeTypeValue, NODE_TYPE_GROUPS } from '@services/modeling.service';
import { LibraryItem } from '@components/pipelineNodes/PipelineNodeList.component';
import { DashboardIcon } from '@components/pipelineNodes/PipelineNodeIcon.component';
import { MissionControlNodeProps } from './MissionControlNode.component';
import SourceIcon from '@components/sources/SourceIcon.component';


const Loader = styled.div`
position: fixed;
top: 50%;
left: 50%;
z-index: 2;
width: 100px;
height: 85px;
background: rgba(0, 0, 0, 0.5);
color: white;
border-radius: 10px;
line-height: 100px;
text-align: center;
`


const NodeCardStyles = styled.div<{active?: boolean}>`
padding: 15px;
width: 400px;

${props => props.active && css`
    .library-item {
        background-color: var(--pliable-yellow-light) !important;

    }
`}

`

export interface BuildErrorProps {
    buildException?: BuildException;
    objectType: string;
    objectId: string;
}


export const BuildError = (props: BuildErrorProps) => {

    if (props.buildException && props.buildException.error_type == 'MissingMappingException') {
        return <Danger>
            <div>
                <strong>Mapping Error</strong><br />
                <p>{props.buildException.message}</p>
                <Link className="btn btn-light" to={`/business-object/${props.objectId}/fields`}>Fix</Link>
            </div>
        </Danger>
    } else if (props.buildException && props.buildException.error_type == 'MissingRelationshipLookupParametersException') {
        return <Danger>
            <div>
                <strong>Relationship Error</strong><br />
                <p>{props.buildException.message}</p>
                <Link className="btn btn-light" to={`/relationship/${props.buildException.relationship_ref}`}>Fix</Link>
            </div>
        </Danger>
    } else if (props.buildException && ['ModelBuildException'].includes(props.buildException.error_type)) {
        return <Danger>
            <div>
                <strong>Build Error</strong><br />
                <BrokenWords>
                    {props.buildException.message}
                </BrokenWords>
            </div>
        </Danger>
    } else if (props.buildException && ['BadSQLException'].includes(props.buildException.error_type)) {
        return <Danger>
            <div>
                <strong>Build Error</strong><br />
                <BrokenWords>
                    {props.buildException.message}
                </BrokenWords>
                <p>Might you have a SQL error in a custom formula?</p>
            </div>
        </Danger>
    }

    return <Danger>
        <div>
            <strong>{props.buildException ? props.buildException.error_type : 'Unknown Error'}</strong><br />
            <BrokenWords>
                {props.buildException && props.buildException.message}
            </BrokenWords>
        </div>
    </Danger>
}

interface RelationshipError {
    relationshipId: string;
    parentObjectName: string;
    error: 'AMBIGUOUS' | 'ORPHAN';
}

interface NodeCardProps {
    nodeData: MissionControlDataFlowNode;
    onMouseEnter: () => any;
    onMouseLeave: () => any;
    faded?: boolean;
    // buildExecution?: BuildExecution;
    modelStatus?: ModelStatus;
    onBuild?: () => any;
    onDelete?: () => any;
    onConnect?: () => any;
    onNavigate?: () => any;
    relationshipErrors?: RelationshipError[];
    isShowingLineage?: boolean;
    onToggleShowLineage: () => any;

}


export const CustomHandle = styled(Handle)`
visibility: hidden;
width: 16px;
height: 16px;
`

interface MissionControlNodeCardProps {
    data: MissionControlNodeProps;
    navigateOnClick?: boolean;
    compact?: boolean;
    className?: string;
    onNavigate?: () => void;
}

export const MissionControlNodeCard = (props: MissionControlNodeCardProps) => {
    const navigate = useNavigate();
    const handleClick = useCallback(() => {
        if (props.navigateOnClick) {
            if (props.onNavigate) {
                props.onNavigate();
            }
            switch (props.data.nodeType) {
                case 'ANALYSIS':
                    navigate(`/charts/${props.data.objectId}`);
                    break;
                case 'DASHBOARD':
                    navigate(`/dashboards/${props.data.objectId}`);
                    break;
                case 'MEASURE':
                    navigate(`/metrics/${props.data.objectId}`);
                    break;
                default:
                    navigate(`/node/${props.data.objectId}`);
            }
        }
    }, [props.data.objectId, props.navigateOnClick]);
    switch (props.data.nodeType) {
        case 'MEASURE':
            return <LibraryItem
                className={props.className}
                topBorder="green"
                description={props.data.description || 'No description'}
                rounded
                compact={props.compact}
                itemType="measure"
                name={props.data.title}
                onClick={handleClick}
            >
                <DashboardIcon icon="mdi mdi-pound-box" bgColor="success" compact />
            </LibraryItem>;
        case 'ANALYSIS':
            return <LibraryItem
                className={props.className}
                topBorder="pink"
                description={props.data.description || 'No description'}
                rounded
                compact={props.compact}
                itemType="analysis"
                name={props.data.title || 'Unnamed Chart'}
                onClick={handleClick}
            >
                <DashboardIcon icon="mdi mdi-chart-box" bgColor="pink" compact />
            </LibraryItem>;
        case 'DASHBOARD':
            return <LibraryItem
                className={props.className}
                topBorder="blue"
                description={props.data.description || 'No description'}
                rounded
                compact={props.compact}
                itemType="dashboard"
                name={props.data.title}
                onClick={handleClick}
            >
                <DashboardIcon icon="mdi mdi-view-dashboard" bgColor="blue" compact />
            </LibraryItem>;
        default:
            const nodeTypeConfig = getNodeTypeConfigFromNodeTypeValue(props.data.nodeType);
            const lastBuild = props.data.lastBuildCompleted ? parseDateString(props.data.lastBuildCompleted) : undefined;
            return <LibraryItem
                className={props.className}
                topBorder={nodeTypeConfig.group.color}
                description={props.data.description || 'No description'}
                rounded
                compact={props.compact}
                totalRecords={props.data.totalRecords}
                lastBuild={lastBuild}
                itemType={props.data.nodeType}
                name={props.data.title}
                onClick={handleClick}

            >
                {/* Here we want to show the source ID if the node has a source ID. */}
                {props.data.sourceId && <SourceIcon sourceId={props.data.sourceId} compact={props.compact} />}
                {!props.data.sourceId && <DashboardIcon icon={nodeTypeConfig.nodeType.icon} bgColor={nodeTypeConfig.group.color} compact />} 
            </LibraryItem>
    }
}
const NodeCard = (props: NodeProps<NodeCardProps>) => {
    return <NodeCardStyles active={props.data.isShowingLineage}>
        <div>
            <MissionControlNodeCard
                data={props.data.nodeData.data}
                navigateOnClick
                onNavigate={props.data.onNavigate} 
            />
        </div>
        <>
        
          <CustomHandle  type="source" position={Position.Right} id="b">
          </CustomHandle>
          <CustomHandle  type="target" position={Position.Left} id="d">
          </CustomHandle>
        </>
        
    </NodeCardStyles>
}

const nodeTypes = {
  custom: NodeCard,
};

const edgeTypes = {
  cardinal: MissionControlCardinalEdge,
};

const connectionLineStyle = {
    strokeWidth: 2,
    stroke: '#FF9F00',
    strokeDashArray: 5,
};

const fitViewOptions = { padding: 1, duration: 500 };

function randomNumberBetween(a: number, b: number): number {
    return Math.floor(Math.random() * (b - a + 1) + b);
}

const xAxisDefaults = {
    'BUSINESS_OBJECT': 600,
    'RECORD_TYPE': 0,
    'REPORT': 1200,
}

function ViewportChangeLogger() {
    useOnViewportChange({
        onChange: (viewport: Viewport) => setViewportInfo(viewport),
    });
   
    return null;
}


interface DiagramManagerProps {
    focusNodeId?: string;
    onToggleNodeLineage: (nodeId: string) => any;
    nodeData: MissionControlDataFlowNode[];
    edgeData: MissionControlDataFlowEdge[];
    activeOrchestration?: BuildOrchestration;
    autoLayout?: boolean;
    onDeleteEdge: (edgeId: string) => void;
    onClickNode: (nodeId: string, drawerTab?: string) => void;
    onClickEdgeStatusIndicator: (edgeId: string) => void;
    onDeleteNode?: (nodeId: string) => void;
    onConnectNode?: (nodeId: string) => void;
    onBuildNode?: (nodeId: string) => void;
    onNavigate?: () => void; 
};

const POSITION_HIERARCHY = NODE_TYPE_GROUPS.map(ntg => ntg.value).concat(['MEASURE', 'ANALYSIS', 'DASHBOARD']);

function usePrevious(value: any) {
    const ref = useRef();
    useEffect(() => {
      ref.current = value;
    });
    return ref.current;
  }


const getHierarchyKey = (id: string, nodeType: string) => {
    if (id.startsWith('PipelineNode:')) {
        return getGroupValueForNodeType(nodeType);
    }
    return nodeType;
}

export const MissionControlDataFlowDiagramManager = (props: DiagramManagerProps) => {
    const [reactFlowInstance, setReactFlowInstance] = useState<ReactFlowInstance|null>(null);

    const [nodes, setNodes] = useState<Node<NodeCardProps>[]>([]);
    const [edges, setEdges] = useState<Edge<MissionControlEdgeProps>[]>([]);


    const [nodePositions, setNodePositions] = useImmer<{[key: string]: XYPosition}>({});

    const [previousOrchestrationStatus, setPreviousOrchestrationStatus] = useState('');
    const container = useRef<HTMLDivElement>(null);

    const [focusedEdge, setFocusedEdge] = useState('');

    const previousViewport = getViewportInfo();

    const tenantId = AuthService.getInstance().getTenantId();

    const onReactFlowInit = useCallback((instance: ReactFlowInstance) => {
        setReactFlowInstance(instance);
    }, []);

    const persistViewport = useCallback(() => {
        if (reactFlowInstance) {
          const flow = reactFlowInstance.toObject();
          setViewportInfo(flow.viewport);
        }
      }, [reactFlowInstance]);

    const isValidConnectionCallback = useCallback((connection: Connection) => {
        return isValidConnection(connection, nodes, edges);
    }, [nodes, edges]);

    // When they click on the 
    const onPaneClick = useCallback(() => {
        setFocusedEdge('');
    }, []);
    const positionsCacheKey = useMemo(() => {
        return `mc_node_positions_3_${tenantId}`
    }, [tenantId]);

    const zoomToFit = useCallback(() => {
        if (!reactFlowInstance) {
            return;
        }
        reactFlowInstance.fitView({
            padding: 50,
            nodes: nodes.map(n => {
                return {id: n.id}
            }),
            duration: 5,
        });
    }, [reactFlowInstance, nodes]);

    const zoomToFitNode = useCallback((nodeId: string) => {
        if (!reactFlowInstance) {
            return;
        }
        reactFlowInstance.fitView({
            padding: 50,
            minZoom: 1,
            maxZoom: 5,
            nodes: [{id: nodeId}],
            duration: 500,
        });
    }, [reactFlowInstance]);

    useEffect(() => {
        if (props.focusNodeId) {
            setTimeout(() => {
                zoomToFitNode(props.focusNodeId as string);
            }, 100)
            
        }
    }, [props.focusNodeId, zoomToFitNode]);


    const [ranInitialAutoLayout, setRanInitialAutoLayout] = useState(false);


    const onToggleShowNodeLineage = useCallback((nodeId: string) => {
        props.onToggleNodeLineage(nodeId);
    }, [props.onToggleNodeLineage]);

    useEffect(() => {
        if (props.nodeData.length == 0) {
            return;
        }
        // Now set the nodes and edges
        let newNodes: Node<NodeCardProps>[] = [];

        // Store the current build status for each node in a lookup table for easy access


        newNodes = props.nodeData.map(n => {
            let position: XYPosition = {
                x: 0, y: 0
            };

            
            const nodeProps: Node<NodeCardProps> = {
                id: n.id,
                position: position,
                type: 'custom',
                data: {
                    modelStatus: props.activeOrchestration?.model_statuses[n.data.objectId],
                    nodeData: n,
                    isShowingLineage: props.focusNodeId == n.id,
                    onToggleShowLineage: () => {
                        onToggleShowNodeLineage(n.id);
                    },
                    onMouseEnter: () => {

                    },
                    onMouseLeave: () => {

                    },
                    faded: false,
                    onBuild: undefined,
                    onDelete: undefined,
                    onConnect: undefined,
                    onNavigate: props.onNavigate,
                    
                },
            }

            // onBuild: () => {
            //     if (props.onBuildNode) {
            //         props.onBuildNode(n.id);
            //     }
            // },
            // onDelete: () => {
            //     if (props.onDeleteNode) {
            //         props.onDeleteNode(n.id);
            //     }
            // },
            // onConnect: () => {
            //     if (props.onConnectNode) {
            //         props.onConnectNode(n.id);

            //     }
            // }

            if (props.onBuildNode) {
                nodeProps.data.onBuild = () => {
                    props.onBuildNode!(n.id);
                }
            }
            if (props.onDeleteNode) {
                nodeProps.data.onDelete = () => {
                    props.onDeleteNode!(n.id);
                }
            }
            if (props.onConnectNode) {
                nodeProps.data.onConnect = () => {
                    props.onConnectNode!(n.id);
                }
            }
            return nodeProps;
        });

        const newNodeIds = newNodes.map(n => n.id);


        let newEdges: Edge[] = [];
        newEdges = props.edgeData.filter(e => {
            return newNodeIds.includes(e.source) && newNodeIds.includes(e.target);
            
        }).map(e => {

            let executionStatus = null;
            if (!!props.activeOrchestration && !['COMPLETE', 'ERROR'].includes(props.activeOrchestration.status)) {
                // If the target is being built, then set the execution status of the edge to ACTIVE
                const targetId = e.target.split(':')[1];
                if (props.activeOrchestration.model_statuses.hasOwnProperty(targetId) && !!props.activeOrchestration.model_statuses[targetId]) {
                    executionStatus = 'ACTIVE';

                // If not, set it to EXCLUDED so we can gray it out
                } else {
                    executionStatus = 'EXCLUDED';
                }
            }

            let edgeColor = '#A29F9C';
            // if (e.data.type == 'NODE_RELATIONSHIP') {
            //     edgeColor = '#A207F7';
            // } else if (['MEASURE', 'MEASURE_CALCULATION'].includes(e.data.type as string)) {
            //     edgeColor = '#08D945';
            // }

            
            const isReport = e.source.startsWith('DataMart:') || e.target.startsWith('DataMart:');
            return {
                'id': e.id,
                'source': e.source,
                'target': e.target,
                'sourceHandle': 'c',
                'targetHandle': 'a',
                'interactionWidth': 50,
                // 'type': 'cardinal',
                markerEnd: {
                    type: MarkerType.ArrowClosed,
                    width: 20,
                    height: 20,
                    color: edgeColor,
                },
                style: {
                    strokeWidth: 2,
                    stroke: edgeColor,
                },

                'data': Object.assign({}, e.data, {
                    'executionStatus': executionStatus,
                    'type': e.data.type,

                    // No actions on report connections
                    'showActions': !isReport && (focusedEdge == e.id),
                    'onClick': () => {
                        if(focusedEdge != e.id) {
                            setFocusedEdge(e.id);
                        } else{
                            setFocusedEdge('');
                        }
                    },
                    'statusIndicator': {
                        type: e.data.status_indicator,
                    },
                    'onClickStatusIndicator': () => {
                        props.onClickEdgeStatusIndicator(e.id)
                    },
                    'onDelete': () => {
                        props.onDeleteEdge(e.id);
                    }
                }),
            }
        });

        const positions = autoLayout(newNodes, newEdges, 'LINEAR', 600, 100);
        


        newNodes = newNodes.map( n => {
            if (positions.hasOwnProperty(n.id)) {
               
                n.position = positions[n.id];

            }
            return n;
        });
        setRanInitialAutoLayout(true);
        
        // Unclear why we need to do this but this prevents DH-1071 where edges would remain even though they weren't in newEdges
        setNodes([]);
        setEdges([]);
        setTimeout(() => {
            setNodes(newNodes);
            setEdges(newEdges);
        })

        // setTimeout(() => {
        //     zoomToFit();
        // }, 100);
    }, [props.nodeData, props.edgeData, props.activeOrchestration, focusedEdge, ranInitialAutoLayout]);

    // useEffect(() => {
    //     setTimeout(() => {
    //         zoomToFit();
    //     }, 100);
    // }, [nodes])

    // const onNodesChange = useCallback((changes: NodeChange[]) => {
    //     if (changes.length === 0 || changes[0].type !== 'position') {
    //         return;
    //     }
    //     const updatedPositions = Object.assign({}, nodePositions);
    //     changes.forEach(c => {
    //         if (c.type === 'position' && c.position) {
    //             updatedPositions[c.id] = c.position;
    //         }
    //     });
    //     setNodePositions(updatedPositions);
    // }, [nodePositions]);


    if (nodes.length) {
        return <div style={{width: '100%', height: '100%'}} ref={container}>
            <ReactFlow
                nodes={nodes}
                edges={edges}
                defaultViewport={previousViewport}
                onPaneClick={onPaneClick}
                isValidConnection={isValidConnectionCallback}
                onInit={onReactFlowInit}
                nodesConnectable={false}
                edgeTypes={edgeTypes}
                draggable={false}
                minZoom={.001}
                nodeTypes={nodeTypes}
                fitView={!previousViewport}
                fitViewOptions={fitViewOptions}
                connectionLineComponent={MissionControlConnectionLine}
                connectionLineStyle={connectionLineStyle}
                style={{background: "#F9F7F5"}}
                proOptions={{hideAttribution: true}}
            >
                <MiniMap position="bottom-right" pannable/>
                <Controls
                        showZoom
                        showFitView
                        showInteractive={false}
                >
                </Controls>
                <ViewportChangeLogger/>
            </ReactFlow>
        </div>
    }
    return <></>;
}




